import {
  EMarketingCampaignStatus,
  EMarketingChannel,
  EMarketingConsentStatus,
  EMarketingContactEventType,
  EMarketingContactSource,
  EMarketingEnrollmentStatus,
  EMarketingFlowStatus,
  EMarketingFlowTrigger,
  EMarketingTemplateCategory,
  EMarketingTemplateStatus,
  MarketingCampaign,
  MarketingContact,
  MarketingContactEvent,
  MarketingFlow,
  MarketingFlowEnrollment,
  MarketingFlowStep,
  MarketingTemplate
} from "../marketing.types";

export interface MockContact extends MarketingContact {
  ordersCount: number;
  totalSpend: number;
}

const daysAgo = (days: number): string =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

const daysAhead = (days: number): string =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

const NAMES: Array<string | null> = [
  "Koffi Kouassi",
  "Awa Diabaté",
  "Fatou Traoré",
  "Yao N'Guessan",
  "Aïcha Koné",
  "Ibrahim Cissé",
  "Mariam Ouattara",
  "Nadège Bamba",
  null,
  "Adjoua Kouamé",
  "Moussa Sangaré",
  "Affoué Yao",
  "Sekou Doumbia",
  null,
  "Aya Brou",
  "Drissa Coulibaly",
  "Akissi Konan",
  "Bakary Touré",
  "Amenan Kablan",
  null,
  "Souleymane Fofana",
  "Ahou Assi",
  "Lacina Silué",
  "Rokia Dembélé"
];

const SOURCES = [
  EMarketingContactSource.CtwaAd,
  EMarketingContactSource.WhatsApp,
  EMarketingContactSource.Order,
  EMarketingContactSource.ResellerSignup,
  EMarketingContactSource.AppInstall
];

const buildContacts = (): MockContact[] =>
  NAMES.map((name, index) => {
    const appInstalled = index % 3 === 0;
    const ordersCount = index % 5;
    const optedOut = index % 7 === 6;

    return {
      id: `mock-contact-${index + 1}`,
      phoneNumber: `2250${(700000000 + index * 1234567).toString().slice(0, 9)}`,
      name,
      source: SOURCES[index % SOURCES.length],
      customerId: index % 2 === 0 ? `mock-customer-${index}` : null,
      resellerId: appInstalled ? `mock-reseller-${index}` : null,
      appInstalled,
      lastOrderAt: ordersCount > 0 ? daysAgo((index * 7) % 90) : null,
      lastAppActivityAt: appInstalled ? daysAgo(index % 25) : null,
      whatsappConsentStatus: optedOut
        ? EMarketingConsentStatus.OptedOut
        : index % 2 === 0
          ? EMarketingConsentStatus.OptedIn
          : EMarketingConsentStatus.Unknown,
      whatsappConsentUpdatedAt: index % 2 === 0 ? daysAgo(index % 40) : null,
      createdAt: daysAgo(90 - index * 3),
      updatedAt: daysAgo(index % 10),
      ordersCount,
      totalSpend: ordersCount * (8000 + index * 997)
    };
  });

export const contacts: MockContact[] = buildContacts();

const EVENT_CYCLE: Array<[EMarketingContactEventType, EMarketingChannel | null]> =
  [
    [EMarketingContactEventType.ProspectEntered, EMarketingChannel.WhatsApp],
    [EMarketingContactEventType.ProductView, null],
    [EMarketingContactEventType.CartCreated, null],
    [EMarketingContactEventType.OrderPlaced, EMarketingChannel.WhatsApp],
    [EMarketingContactEventType.OrderDelivered, null],
    [EMarketingContactEventType.MessageSent, EMarketingChannel.WhatsApp],
    [EMarketingContactEventType.AppOpen, null],
    [EMarketingContactEventType.MessageSent, EMarketingChannel.Push]
  ];

export const eventsByContactId = new Map<string, MarketingContactEvent[]>(
  contacts.map((contact, contactIndex) => [
    contact.id,
    Array.from({ length: 3 + (contactIndex % 4) }, (_, eventIndex) => {
      const [type, channel] =
        EVENT_CYCLE[(contactIndex + eventIndex) % EVENT_CYCLE.length];

      return {
        id: `mock-event-${contact.id}-${eventIndex}`,
        contactId: contact.id,
        type,
        channel,
        metadata: null,
        createdAt: daysAgo(eventIndex * 4 + (contactIndex % 3))
      };
    })
  ])
);

export const templates: MarketingTemplate[] = [
  {
    id: "mock-template-1",
    name: "welcome_offer_v1",
    language: "fr",
    category: EMarketingTemplateCategory.Marketing,
    status: EMarketingTemplateStatus.Approved,
    bodyText:
      "Salut {{1}}, cadeau de bienvenue : -10% sur votre première commande, valable 48h. Installez l'app et profitez-en !",
    variableCount: 1,
    metaTemplateId: "meta-1001",
    createdAt: daysAgo(40),
    updatedAt: daysAgo(38)
  },
  {
    id: "mock-template-2",
    name: "winback_10_v1",
    language: "fr",
    category: EMarketingTemplateCategory.Marketing,
    status: EMarketingTemplateStatus.Approved,
    bodyText:
      "{{1}}, ça fait un moment ! Voici -10% pour votre prochaine commande : {{2}}. Livraison partout à Abidjan, paiement à la livraison.",
    variableCount: 2,
    metaTemplateId: "meta-1002",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(28)
  },
  {
    id: "mock-template-3",
    name: "post_purchase_thanks",
    language: "fr",
    category: EMarketingTemplateCategory.Utility,
    status: EMarketingTemplateStatus.Approved,
    bodyText:
      "Merci pour votre commande {{1}} ! Pour aller avec, découvrez nos best-sellers accessoires.",
    variableCount: 1,
    metaTemplateId: "meta-1003",
    createdAt: daysAgo(25),
    updatedAt: daysAgo(20)
  },
  {
    id: "mock-template-4",
    name: "new_arrivals_weekly",
    language: "fr",
    category: EMarketingTemplateCategory.Marketing,
    status: EMarketingTemplateStatus.Pending,
    bodyText:
      "Les nouveautés de la semaine chez ChapShop, {{1}} : jetez un œil avant que ça parte !",
    variableCount: 1,
    metaTemplateId: null,
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3)
  },
  {
    id: "mock-template-5",
    name: "cart_reminder_v2",
    language: "fr",
    category: EMarketingTemplateCategory.Marketing,
    status: EMarketingTemplateStatus.Rejected,
    bodyText:
      "{{1}}, votre {{2}} vous attend toujours dans le panier. On vous le réserve ?",
    variableCount: 2,
    metaTemplateId: null,
    createdAt: daysAgo(12),
    updatedAt: daysAgo(10)
  },
  {
    id: "mock-template-6",
    name: "promo_flash_en",
    language: "en",
    category: EMarketingTemplateCategory.Marketing,
    status: EMarketingTemplateStatus.Draft,
    bodyText: "Hi {{1}}, flash sale today only — up to -30% on electronics!",
    variableCount: 1,
    metaTemplateId: null,
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1)
  }
];

export const campaigns: MarketingCampaign[] = [
  {
    id: "mock-campaign-1",
    name: "Relance montres — juillet",
    segmentDefinition: { hasOrdered: true, inactiveForDays: 30 },
    messageText:
      "Bonjour {{firstname}}, nos montres font leur retour en stock — jetez un œil !",
    templateName: "winback_10_v1",
    templateParams: ["{{firstname}}", "WELCOME10"],
    channel: null,
    status: EMarketingCampaignStatus.Completed,
    sentCount: 118,
    skippedCount: 24,
    launchedAt: daysAgo(3),
    createdAt: daysAgo(5),
    updatedAt: daysAgo(3)
  },
  {
    id: "mock-campaign-2",
    name: "Nouveautés semaine 29",
    segmentDefinition: { hasOrdered: true },
    messageText: null,
    templateName: "new_arrivals_weekly",
    templateParams: ["{{firstname}}"],
    channel: EMarketingChannel.WhatsApp,
    status: EMarketingCampaignStatus.Completed,
    sentCount: 342,
    skippedCount: 51,
    launchedAt: daysAgo(9),
    createdAt: daysAgo(10),
    updatedAt: daysAgo(9)
  },
  {
    id: "mock-campaign-3",
    name: "Push clients app — test",
    segmentDefinition: { appStatus: undefined, hasOrdered: undefined },
    messageText: "{{firstname}}, une sélection rien que pour vous dans l'app.",
    templateName: null,
    templateParams: null,
    channel: EMarketingChannel.Push,
    status: EMarketingCampaignStatus.Draft,
    sentCount: 0,
    skippedCount: 0,
    launchedAt: null,
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1)
  },
  {
    id: "mock-campaign-4",
    name: "Prospects sans commande",
    segmentDefinition: { hasOrdered: false, excludeOptedOut: true },
    messageText:
      "Bonjour {{firstname}}, bienvenue chez ChapShop ! Livraison gratuite à Abidjan, paiement à la livraison.",
    templateName: "welcome_offer_v1",
    templateParams: ["{{firstname}}"],
    channel: null,
    status: EMarketingCampaignStatus.Draft,
    sentCount: 0,
    skippedCount: 0,
    launchedAt: null,
    createdAt: daysAgo(1),
    updatedAt: daysAgo(1)
  },
  {
    id: "mock-campaign-5",
    name: "Black Friday teaser",
    segmentDefinition: { minOrders: 2 },
    messageText: null,
    templateName: "promo_flash_en",
    templateParams: ["{{firstname}}"],
    channel: EMarketingChannel.WhatsApp,
    status: EMarketingCampaignStatus.Failed,
    sentCount: 12,
    skippedCount: 3,
    launchedAt: daysAgo(15),
    createdAt: daysAgo(16),
    updatedAt: daysAgo(15)
  }
];

export const flows: MarketingFlow[] = [
  {
    id: "mock-flow-1",
    name: "Relance post-achat",
    trigger: EMarketingFlowTrigger.OrderPlaced,
    status: EMarketingFlowStatus.Active,
    createdAt: daysAgo(20),
    updatedAt: daysAgo(2)
  },
  {
    id: "mock-flow-2",
    name: "Bienvenue sur l'app",
    trigger: EMarketingFlowTrigger.ResellerSignup,
    status: EMarketingFlowStatus.Active,
    createdAt: daysAgo(14),
    updatedAt: daysAgo(14)
  },
  {
    id: "mock-flow-3",
    name: "Winback clients silencieux",
    trigger: EMarketingFlowTrigger.Manual,
    status: EMarketingFlowStatus.Paused,
    createdAt: daysAgo(8),
    updatedAt: daysAgo(4)
  }
];

export const steps: MarketingFlowStep[] = [
  {
    id: "mock-step-1",
    flowId: "mock-flow-1",
    position: 0,
    delayDays: 7,
    channel: EMarketingChannel.WhatsApp,
    messageText:
      "Merci pour votre commande {{firstname}} ! Pour aller avec, voici nos best-sellers accessoires.",
    templateName: "post_purchase_thanks",
    templateParams: ["{{firstname}}"]
  },
  {
    id: "mock-step-2",
    flowId: "mock-flow-1",
    position: 1,
    delayDays: 7,
    channel: EMarketingChannel.WhatsApp,
    messageText:
      "{{firstname}}, ces accessoires partent vite : chargeur rapide, support voiture, verre trempé.",
    templateName: null,
    templateParams: null
  },
  {
    id: "mock-step-3",
    flowId: "mock-flow-1",
    position: 2,
    delayDays: 14,
    channel: null,
    messageText: "{{firstname}}, une sélection pour vous — très populaire en ce moment.",
    templateName: "new_arrivals_weekly",
    templateParams: ["{{firstname}}"]
  },
  {
    id: "mock-step-4",
    flowId: "mock-flow-2",
    position: 0,
    delayDays: 2,
    channel: null,
    messageText:
      "{{firstname}}, la montre dorée que vous avez regardée est toujours disponible. Un coup d'œil ?",
    templateName: null,
    templateParams: null
  },
  {
    id: "mock-step-5",
    flowId: "mock-flow-2",
    position: 1,
    delayDays: 6,
    channel: null,
    messageText:
      "Nouveauté, {{firstname}} : une nouvelle montre femme vient d'arriver. Elle pourrait vous plaire.",
    templateName: null,
    templateParams: null
  },
  {
    id: "mock-step-6",
    flowId: "mock-flow-3",
    position: 0,
    delayDays: 0,
    channel: EMarketingChannel.WhatsApp,
    messageText: null,
    templateName: "winback_10_v1",
    templateParams: ["{{firstname}}", "WINBACK10"]
  }
];

export const enrollments: MarketingFlowEnrollment[] = [
  {
    id: "mock-enrollment-1",
    flowId: "mock-flow-1",
    contactId: contacts[1].id,
    position: 1,
    nextRunAt: daysAhead(2),
    status: EMarketingEnrollmentStatus.Active,
    enrolledAt: daysAgo(5),
    updatedAt: daysAgo(1)
  },
  {
    id: "mock-enrollment-2",
    flowId: "mock-flow-1",
    contactId: contacts[3].id,
    position: 2,
    nextRunAt: daysAhead(9),
    status: EMarketingEnrollmentStatus.Active,
    enrolledAt: daysAgo(12),
    updatedAt: daysAgo(2)
  },
  {
    id: "mock-enrollment-3",
    flowId: "mock-flow-1",
    contactId: contacts[5].id,
    position: 3,
    nextRunAt: null,
    status: EMarketingEnrollmentStatus.Completed,
    enrolledAt: daysAgo(30),
    updatedAt: daysAgo(4)
  },
  {
    id: "mock-enrollment-4",
    flowId: "mock-flow-1",
    contactId: contacts[6].id,
    position: 1,
    nextRunAt: null,
    status: EMarketingEnrollmentStatus.Stopped,
    enrolledAt: daysAgo(9),
    updatedAt: daysAgo(6)
  },
  {
    id: "mock-enrollment-5",
    flowId: "mock-flow-2",
    contactId: contacts[0].id,
    position: 0,
    nextRunAt: daysAhead(1),
    status: EMarketingEnrollmentStatus.Active,
    enrolledAt: daysAgo(1),
    updatedAt: daysAgo(1)
  }
];
